import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../widgets/Head';
import * as css from './StrategySession.m.css';
import icache from '@dojo/framework/core/middleware/icache';
import Testimonials from '../widgets/Testimonials';
import ContentWrapper from '../widgets/ContentWrapper';
import Hero from '../widgets/Hero';

const checkIcon = require('../assets/images/icon-solid/check.svg');

const factory = create({ icache });

export const StrategySession = factory(function StrategySession({ middleware: { icache } }) {
	const submitError = icache.getOrSet('submitError', false);

	return (
		<div classes={css.root}>
			<Head
				title="Schedule a Strategy Session | SitePen"
				description="With offices throughout the US and UK, we’re available to to chat at your convenience. Send us a note to hello@sitepen.com."
			/>
			<Hero background="level3Blue" paddingTop="sm" paddingBottom="sm">
				{{
					xlTitle: (
						<virtual>
							Let's Talk
							<br />
							Strategy
						</virtual>
					),
					blurb: (
						<div>
							<h2 classes={css.subHeading}>
								<b>Connect with one of our engineering leaders</b>
								<br /> for a directed discussion to satisfy your current objectives.
								Potential topics may include:
							</h2>
							<ul classes={css.iconList}>
								<li classes={css.item}>
									<img classes={css.img} src={checkIcon} alt="" />
									Architecture and tech stack decisions
								</li>
								<li classes={css.item}>
									<img classes={css.img} src={checkIcon} alt="" />
									Your development goals & challenges
								</li>
								<li classes={css.item}>
									<img classes={css.img} src={checkIcon} alt="" />
									Approach to modernize your legacy application
								</li>
								<li classes={css.item}>
									<img
										loading="lazy"
										classes={css.img}
										src={checkIcon}
										alt="knowledge icon"
									/>
									Guidance on implementing automated testing
								</li>
							</ul>
						</div>
					)
				}}
			</Hero>
			<ContentWrapper>
				<div classes={css.content}>
					<div classes={css.top}>
						<div classes={css.leading}></div>
						<form
							classes={[css.trailing, css.form]}
							onsubmit={(event: Event) => {
								event.stopPropagation();
								event.preventDefault();
								icache.set('submitError', false);

								const url =
									'https://script.google.com/macros/s/AKfycbxGw_dH7fz1iDh1A619ssMuXEkZX--5MsKTpIKUS_KSO9n-LyiX18zEhVDkCl-IXzoF/exec';
								const form = event.target as HTMLFormElement;

								const gotcha =
									(form.elements.namedItem('_gotcha') as HTMLInputElement) ||
									(form as any)['_gotcha'];
								if (gotcha && gotcha.value) {
									// Bot: silently drop
									return false;
								}

								const { elements } = form;
								const fields: { name: string; value: string }[] = [];

								for (let i = 0; i < elements.length; i++) {
									const field = elements[i] as
										| HTMLInputElement
										| HTMLTextAreaElement;
									if (!field.name || field.name === '_gotcha') continue;

									let value = field.value;
									if (
										field instanceof HTMLInputElement &&
										field.type === 'checkbox'
									) {
										value = field.checked ? 'on' : '';
									}

									fields.push({ name: field.name, value });
								}
								const referrer = document.referrer;
								const matches = referrer.match(/blog\/(.*)/);
								let blogSlug = '';
								if (matches && matches[1]) {
									blogSlug = matches[1];
								}
								const payload = JSON.stringify({
									submittedAt: Date.now(),
									context: {
										pageUri: blogSlug
											? `www.sitepen.com/blog/${blogSlug}`
											: 'www.sitepen.com/strategy-session',
										pageName: 'Sitepen Website Contact Form'
									},
									fields
								});

								// Prefer sendBeacon for reliability on navigation
								if (navigator.sendBeacon) {
									const blob = new Blob([payload], {
										type: 'text/plain;charset=UTF-8'
									});
									navigator.sendBeacon(url, blob);
								} else {
									// Fallback: fire-and-forget fetch with no-cors
									fetch(url, {
										method: 'POST',
										mode: 'no-cors',
										headers: {
											'Content-Type': 'text/plain;charset=UTF-8'
										},
										body: payload
									}).catch(() => {
										icache.set('submitError', true);
									});
								}
								window.location.href = '/contact-thank-you';
								return false;
							}}
						>
							<h1 classes={css.formTitle}>Schedule a Strategy Session</h1>
							<div classes={css.row}>
								<div classes={css.field}>
									<label classes={css.label} for="firstname">
										First Name
										<span classes={css.required} aria-hidden="true">
											*
										</span>
									</label>
									<input
										classes={css.input}
										id="firstname"
										name="firstname"
										type="text"
										required
										autocomplete="given-name"
									/>
								</div>
								<div classes={css.field}>
									<label classes={css.label} for="lastname">
										Last Name
										<span classes={css.required} aria-hidden="true">
											*
										</span>
									</label>
									<input
										classes={css.input}
										id="lastname"
										name="lastname"
										type="text"
										required
										autocomplete="family-name"
									/>
								</div>
							</div>
							<div classes={css.row}>
								<label tabindex="-1" aria-hidden="true" classes={css.gotcha}>
									gotcha label
									<input
										tabindex="-1"
										aria-hidden="true"
										id="_gotcha"
										name="_gotcha"
										type="text"
										autocomplete="off"
									/>
								</label>
								<div classes={css.field}>
									<label classes={css.label} for="company">
										Company Name
										<span classes={css.required} aria-hidden="true">
											*
										</span>
									</label>
									<input
										classes={css.input}
										id="company"
										name="company"
										type="text"
										required
										autocomplete="organization"
									/>
								</div>
								<div classes={css.field}>
									<label classes={css.label} for="phone">
										Phone Number
										<span classes={css.required} aria-hidden="true">
											*
										</span>
									</label>
									<input
										classes={css.input}
										id="phone"
										name="phone"
										type="tel"
										required
										autocomplete="tel"
										minLength={7}
									/>
								</div>
							</div>
							<div classes={css.row}>
								<div classes={css.field}>
									<label classes={css.label} for="email">
										Email
										<span classes={css.required} aria-hidden="true">
											*
										</span>
									</label>
									<input
										classes={css.input}
										id="email"
										name="email"
										type="email"
										required
										autocomplete="email"
									/>
								</div>
							</div>
							<div classes={css.row}>
								<div classes={css.field}>
									<label classes={css.label} for="what_is_your_role_">
										What is your role?
										<span classes={css.required} aria-hidden="true">
											*
										</span>
									</label>
									<select
										required
										classes={[css.input, css.select]}
										id="what_is_your_role_"
										name="what_is_your_role_"
									>
										<option value="">Please Select</option>
										<option value="Technical Lead">Technical Lead</option>
										<option value="Team Developer">Team Developer</option>
										<option value="Project Manager">Project Manager</option>
										<option value="Director, VP, C-Level">
											Director, VP, C-Level
										</option>
										<option value="Vendor Relations">Vendor Relations</option>
										<option value="Other">Other</option>
									</select>
								</div>
							</div>
							<div classes={css.row}>
								<div classes={css.field}>
									<label classes={css.label} for="how_would_you_like_to_help_">
										How can we help?
										<span classes={css.required} aria-hidden="true">
											*
										</span>
									</label>
									<textarea
										required
										classes={css.textarea}
										id="how_would_you_like_to_help_"
										name="how_would_you_like_to_help_"
									/>
								</div>
							</div>
							<button type="submit" classes={css.submit}>
								Submit
							</button>
							{submitError && (
								<p classes={css.error}>
									Something went wrong with your form submission. Please try again
								</p>
							)}
						</form>
					</div>
					<div classes={[css.testimonials]}>
						<Testimonials />
					</div>
				</div>
			</ContentWrapper>
		</div>
	);
});

export default StrategySession;
